import apiClient from '../client';
import { ApiResponse } from '../types/common.types';
import { DashboardData, DashboardQueryParams } from '../types/dashboard.types';

/**
 * Dashboard API endpoints
 */
export const dashboardApi = {
  /**
   * Get dashboard data including stats, recent activities, and charts
   * Note: Backend returns data directly, not wrapped in ApiResponse.data
   */
  getDashboardData: async (
    params?: DashboardQueryParams
  ): Promise<DashboardData> => {
    const response = await apiClient.get<any>('/reports/dashboard', { params });
    const raw = response.data;
    const pf = (v: any) => parseFloat(v ?? '0') || 0;
    // Backend returns monetary/decimal fields as string decimals — parse them here
    return {
      ...raw,
      totalDisbursed:        pf(raw.totalDisbursed),
      totalOutstanding:      pf(raw.totalOutstanding),
      totalCollected:        pf(raw.totalCollected),
      collectedToday:        pf(raw.collectedToday),
      collectedThisWeek:     pf(raw.collectedThisWeek),
      collectedThisMonth:    pf(raw.collectedThisMonth),
      totalOverdueAmount:    pf(raw.totalOverdueAmount),
      par30:                 pf(raw.par30),
      npl:                   pf(raw.npl),
      collectionRate:        pf(raw.collectionRate),
      interestIncome:        pf(raw.interestIncome),
      processingFeeIncome:   pf(raw.processingFeeIncome),
      penaltyIncome:         pf(raw.penaltyIncome),
      totalRevenue:          pf(raw.totalRevenue),
    } as DashboardData;
  },

  /**
   * Get recent activities
   */
  getRecentActivities: async () => {
    const response = await apiClient.get<
      ApiResponse<{
        loans: DashboardData['recentLoans'];
        applications: DashboardData['recentApplications'];
        payments: DashboardData['recentPayments'];
      }>
    >('/dashboard/activities');
    if (!response.data.data) {
      throw new Error('No activities data received from server');
    }
    return response.data.data;
  },

  /**
   * Get chart data
   */
  getChartData: async () => {
    const response = await apiClient.get<
      ApiResponse<{
        loanDistribution: DashboardData['loanDistribution'];
        collectionTrends: DashboardData['collectionTrends'];
        monthlyDisbursements: DashboardData['monthlyDisbursements'];
      }>
    >('/dashboard/charts');
    if (!response.data.data) {
      throw new Error('No chart data received from server');
    }
    return response.data.data;
  },

  /**
   * Get alerts (overdue loans and pending approvals)
   */
  getAlerts: async () => {
    const response = await apiClient.get<
      ApiResponse<{
        overdueAlerts: DashboardData['overdueAlerts'];
        pendingApprovals: DashboardData['pendingApprovals'];
      }>
    >('/dashboard/alerts');
    if (!response.data.data) {
      throw new Error('No alerts data received from server');
    }
    return response.data.data;
  },
};
